import {
  cn,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
  useIsMobile,
} from '@design-system';

import {
  InviteClientTabs,
  InviteClientContext,
  useInviteClientProvider,
  useInviteClientTrigger,
  useInviteClientStore,
  EmailDeliverabilityWarningModal,
} from '@/features/invite-client';
import { useFeatureFlags } from '@client/contexts/FeatureFlagsContext';
import { useIsEdjPilotAdvisor } from '@/shared/hooks/use-edj-pilot-advisor';

interface InviteClientDialogContentProps {
  title?: string;
  description?: string;
}

function InviteClientDialogContent({
  title = 'Invite a client',
  description = 'Send an invitation to help your clients plan for long-term care',
}: InviteClientDialogContentProps) {
  const isMobile = useIsMobile();
  const { isInviteClientOpen, clearInviteClientParams } = useInviteClientTrigger();
  const { showAdditionalSettings, showEmailWarningModal } = useInviteClientStore();

  const showStackedDialog = showAdditionalSettings || showEmailWarningModal;

  return (
    <>
      {!isMobile ? (
        <Dialog
          open={isInviteClientOpen}
          onOpenChange={open => {
            if (!open) {
              clearInviteClientParams();
            }
          }}
        >
          <DialogContent
            className={cn(
              'max-h-[90vh] w-full overflow-y-auto transition-all duration-200 sm:max-w-xl',
              {
                'max-h-144 min-h-144 -translate-y-[calc(50%+1rem)] scale-[0.94]': showStackedDialog,
              },
            )}
          >
            <DialogHeader>
              <DialogTitle>{title}</DialogTitle>
              <DialogDescription>{description}</DialogDescription>
            </DialogHeader>
            <InviteClientTabs />
          </DialogContent>
        </Dialog>
      ) : (
        <Drawer
          open={isInviteClientOpen}
          onOpenChange={open => {
            if (!open) {
              clearInviteClientParams();
            }
          }}
        >
          <DrawerContent
            className={cn('max-h-[90vh]! transition-all! duration-200! overflow-y-auto px-4 pb-6', {
              'h-144 bottom-1! scale-[0.96]': showStackedDialog,
            })}
          >
            <DrawerHeader className="text-left">
              <DrawerTitle>{title}</DrawerTitle>
              <DrawerDescription>{description}</DrawerDescription>
            </DrawerHeader>
            <InviteClientTabs />
          </DrawerContent>
        </Drawer>
      )}

      <EmailDeliverabilityWarningModal />
    </>
  );
}

export interface InviteClientDialogProps extends InviteClientDialogContentProps {
  onInviteSuccess?: () => void;
}

export function InviteClientDialog({
  title,
  description,
  onInviteSuccess,
}: InviteClientDialogProps) {
  const { flags } = useFeatureFlags();
  const { clearInviteClientParams } = useInviteClientTrigger();
  const storeValue = useInviteClientProvider({
    onInviteSuccess,
    onClose: clearInviteClientParams,
  });

  const isEdjPilot = useIsEdjPilotAdvisor();
  const hideEmailAndFillIntakeForm = flags?.hideInviteEmailAndFillIntakeForm;

  const dialogDescription =
    hideEmailAndFillIntakeForm || isEdjPilot
      ? 'Get a unique link that you can share with clients through text, social media, or in person.'
      : description;

  return (
    <InviteClientContext.Provider value={storeValue}>
      <InviteClientDialogContent
        title={title}
        description={dialogDescription}
      />
    </InviteClientContext.Provider>
  );
}
